import _ from 'lodash';
import React, { useState } from 'react';
import { Button } from '../../components/actions/Button';
import LoadMoreLess from '../../components/actions/LoadMoreLess';
import { StatGroup } from '../../components/stats/StatGroup';
import { StatListItem } from '../../components/stats/StatListItem';
import { KPI_TAG_METADATA, WarehouseKPI } from '../../dashboard/kpi';
import useActualityAggregationMetricDescriptors from '../hooks/useActualityAggregationMetricDescriptors';
import {
  ActualityAggregatedHqExtendedDataRow,
  ActualityAggregatedHqExtendedStatsDataColumn,
} from '../store/actuality.kpi.types';

export type ActualityAggregatedStatsMode =
  | 'all'
  | 'loadMore'
  | 'fieldsOnly'
  | 'summary';

export type ActualityAggregatedStatsProps = {
  data: ActualityAggregatedHqExtendedDataRow;
  fields?: ActualityAggregatedHqExtendedStatsDataColumn[];
  mode?: ActualityAggregatedStatsMode;
  defaultFieldsLimit?: number;
  defaultFields?: ActualityAggregatedHqExtendedStatsDataColumn[];
  summaryFields?: ActualityAggregatedHqExtendedStatsDataColumn[];
  shouldShowSummaryToggle?: boolean;
};

const ActualityAggregatedStats: React.FC<
  ActualityAggregatedStatsProps
> = props => {
  const {
    data,
    fields,
    mode = 'all',
    defaultFieldsLimit = 5,
    defaultFields,
    summaryFields,
  } = props;

  const [showAll, setShowAll] = useState(false);

  const aggMetricDescriptors = useActualityAggregationMetricDescriptors();
  const groupedDescriptors = _.groupBy(
    _.values(aggMetricDescriptors),
    'category',
  );

  // Determine which fields to use based on mode and props
  const getFieldsToShow = (categoryDescriptors: WarehouseKPI[]) => {
    switch (mode) {
      case 'fieldsOnly':
        // Only show explicitly passed fields
        return _.filter(
          categoryDescriptors,
          d => fields && _.includes(fields, d.id),
        );

      case 'summary':
        if (showAll) {
          // Show all available fields (or filtered fields if provided)
          return fields
            ? _.filter(categoryDescriptors, d => _.includes(fields, d.id))
            : categoryDescriptors;
        } else {
          // Show summary fields
          const fieldsToUse = summaryFields || defaultFields;
          return fieldsToUse
            ? _.filter(categoryDescriptors, d => _.includes(fieldsToUse, d.id))
            : _.take(categoryDescriptors, defaultFieldsLimit);
        }

      case 'loadMore':
        let availableFields: WarehouseKPI[];

        if (fields) {
          // If fields are passed, filter to those fields first
          availableFields = _.filter(categoryDescriptors, d =>
            _.includes(fields, d.id),
          );
        } else if (defaultFields) {
          // Use default fields
          availableFields = _.filter(categoryDescriptors, d =>
            _.includes(defaultFields, d.id),
          );
        } else {
          // Use all available fields
          availableFields = categoryDescriptors;
        }

        // Apply limit based on showAll state
        return showAll
          ? availableFields
          : _.take(availableFields, defaultFieldsLimit);

      case 'all':
      default:
        // Show all fields, optionally filtered by passed fields
        return fields
          ? _.filter(categoryDescriptors, d => _.includes(fields, d.id))
          : categoryDescriptors;
    }
  };

  const hasCategoryStats = mode !== 'fieldsOnly' && _.isEmpty(fields);

  // Calculate total available fields for summary mode
  const getSummaryFieldsCount = () => {
    return _.sum(
      _.map(KPI_TAG_METADATA, (categoryDescriptor, categoryId) => {
        const categoryDescriptors = groupedDescriptors[categoryId] || [];
        const fieldsToUse = summaryFields || defaultFields;

        if (fieldsToUse) {
          return _.filter(categoryDescriptors, d =>
            _.includes(fieldsToUse, d.id),
          ).length;
        } else {
          return Math.min(categoryDescriptors.length, defaultFieldsLimit);
        }
      }),
    );
  };

  // Calculate total available fields for load more functionality
  const getTotalAvailableFields = () => {
    return _.sum(
      _.map(KPI_TAG_METADATA, (categoryDescriptor, categoryId) => {
        const categoryDescriptors = groupedDescriptors[categoryId] || [];

        if (mode === 'summary') {
          // For summary mode, total is all fields (or filtered fields if provided)
          return fields
            ? _.filter(categoryDescriptors, d => _.includes(fields, d.id))
                .length
            : categoryDescriptors.length;
        }

        if (fields) {
          return _.filter(categoryDescriptors, d => _.includes(fields, d.id))
            .length;
        } else if (defaultFields) {
          return _.filter(categoryDescriptors, d =>
            _.includes(defaultFields, d.id),
          ).length;
        } else {
          return categoryDescriptors.length;
        }
      }),
    );
  };

  const getCurrentlyShowingFields = () => {
    return _.sum(
      _.map(KPI_TAG_METADATA, (categoryDescriptor, categoryId) => {
        const categoryDescriptors = groupedDescriptors[categoryId] || [];
        return getFieldsToShow(categoryDescriptors).length;
      }),
    );
  };

  const shouldShowLoadMore =
    mode === 'loadMore' &&
    !showAll &&
    getCurrentlyShowingFields() < getTotalAvailableFields();

  const shouldShowLess =
    mode === 'loadMore' &&
    showAll &&
    getTotalAvailableFields() > defaultFieldsLimit;

  const shouldShowSummaryToggle =
    mode === 'summary' && getSummaryFieldsCount() < getTotalAvailableFields();

  return (
    <>
      {_.map(KPI_TAG_METADATA, (categoryDescriptor, categoryId) => {
        const categoryDescriptors: WarehouseKPI[] =
          groupedDescriptors[categoryId] || [];
        const fieldsToShow = getFieldsToShow(categoryDescriptors);

        if (_.isEmpty(fieldsToShow)) return null;

        const getChildren = () => (
          <>
            {_.map(fieldsToShow, statDescriptor => {
              const value = _.get(data, statDescriptor.id);
              const formattedValue = statDescriptor.getFormatted(value);

              return (
                <StatListItem
                  key={statDescriptor.id}
                  title={statDescriptor.title}
                  value={formattedValue.value}
                  unitOfMeasure={formattedValue.unit}
                  className="!mb-0"
                  inStatView
                />
              );
            })}
          </>
        );

        if (hasCategoryStats) {
          return (
            <StatGroup
              key={categoryId}
              title={categoryDescriptor.title}
              classNameStatGroupItems="divide-y divide-solid divide-menu/75"
              classNameTitle="!text-xs !text-menu-text"
              classNameTitleContainer="!bg-sidebar-header/50 py-1 px-2 w-full gap-2"
              hasNoPadding
              children={getChildren()}
            />
          );
        } else {
          return getChildren();
        }
      })}

      {(shouldShowLoadMore || shouldShowLess) && (
        <div className="flex justify-end pt-4">
          {shouldShowLoadMore && (
            <Button
              label={`Load More... (${getCurrentlyShowingFields()} of ${getTotalAvailableFields()} shown)`}
              buttonSize="xs"
              buttonType="secondary"
              className="!text-xxs rounded-full !bg-opacity-30"
              onPress={() => setShowAll(true)}
            />
          )}

          {shouldShowLess && (
            <Button
              label={`Show Less (${getCurrentlyShowingFields()} shown)`}
              buttonSize="xs"
              buttonType="secondary"
              className="!text-xxs rounded-full !bg-opacity-20"
              onPress={() => setShowAll(false)}
            />
          )}
        </div>
      )}

      {shouldShowSummaryToggle && (
        <LoadMoreLess
          shouldShowLoadMore={shouldShowLoadMore}
          shouldShowLess={shouldShowLess}
          getCurrentlyShowingFields={getCurrentlyShowingFields}
          getTotalAvailableFields={getTotalAvailableFields}
          setShowAll={setShowAll}
        />
      )}
    </>
  );
};

export default ActualityAggregatedStats;
